import { useEffect, useRef, useState } from 'react';
import { Group, MultiSelect } from '@mantine/core';
import logger from '@ayocore/platform/common/helpers/logger';
import { useDealStatusRepository } from '@ayocore/erp/crm/client/rpc';
import { useRepositoryFilter } from '@ayocore/platform/client/components/RepositoryTable/useRepositoryFilter';
import type { DealRepository } from '@ayocore/erp/crm/server/repositories/DealRepository';

/**
 * Фабрика мультиселект-фильтра по статусу сделки.
 * @param dealTypeSlug — если передан, загружает только статусы данного типа сделки.
 *                       Если не передан — показывает все статусы.
 */
export function createFilterByStatus(dealTypeSlug?: string) {
  return useRepositoryFilter<DealRepository>('status', ({ filter, disabled, fromUrl, toUrl }) => {
    const [statusIds, setStatusIds] = useState<string[]>([]);
    const [statuses, setStatuses] = useState<Array<{ value: string; label: string }>>([]);
    const [loading, setLoading] = useState(false);
    const colorMap = useRef<Record<string, string>>({});

    useEffect(() => {
      const loadStatuses = async () => {
        try {
          setLoading(true);
          const statusRepository = dealTypeSlug
            ? useDealStatusRepository().filterByTypeSlug(dealTypeSlug)
            : useDealStatusRepository();
          const rows = await statusRepository.limit(100);
          const map: Record<string, string> = {};
          rows.forEach(s => {
            map[s.id.toString()] = s.color || '#f8f8f8';
          });
          colorMap.current = map;
          setStatuses(
            rows.map(status => ({
              value: status.id.toString(),
              label: status.name || 'Без названия',
            }))
          );
        } catch (error) {
          logger.error('Deal FilterByStatus: failed to load statuses', error);
        } finally {
          setLoading(false);
        }
      };

      loadStatuses();
    }, []);

    useEffect(() => {
      try {
        const raw = fromUrl();
        if (!raw) return;

        let values: string[] = [];
        try {
          const parsed = JSON.parse(raw);
          values = Array.isArray(parsed) ? parsed.map(String) : [String(raw)];
        } catch {
          // поддержка legacy-URL с одним id без JSON-обёртки
          values = [String(raw)];
        }

        if (values.length > 0) {
          setStatusIds(values);
          const ids = values.map(v => parseInt(v, 10)).filter(v => Number.isFinite(v));
          filter(ids.length > 0 ? repo => repo.filterByStatuses(ids) : null);
        }
      } catch (error) {
        logger.warn('Deal FilterByStatus: fromUrl failed', error);
      }
    }, []);

    const handleChange = (values: string[]) => {
      setStatusIds(values);
      const ids = values.map(v => parseInt(v, 10)).filter(v => Number.isFinite(v));
      filter(ids.length > 0 ? repo => repo.filterByStatuses(ids) : null);
      toUrl(values.length > 0 ? JSON.stringify(values) : null);
    };

    return (
      <MultiSelect
        placeholder='Статус сделки...'
        data={statuses}
        value={statusIds}
        onChange={handleChange}
        searchable
        clearable
        size='xs'
        disabled={disabled || loading}
        style={{ minWidth: 220 }}
        renderOption={({ option }) => (
          <Group gap={8} wrap='nowrap'>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: colorMap.current[option.value] || '#3b82f6',
                flexShrink: 0,
              }}
            />
            {option.label}
          </Group>
        )}
      />
    );
  });
}

/** Фильтр по статусу — все типы сделок (для общего списка сделок). */
export const FilterByStatus = createFilterByStatus();
